import React, { useState, useEffect, act } from 'react';
import { approveExtension, pcExtensionWorkFlow } from '@app/api/pre-clearance.api';
import { Modal, DatePicker } from 'antd';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import ExtensionPopup from '@app/components/apps/common/ExtensionPopup';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';

// Define the allowed workflow actions as a union type
type ExtensionWfAction = 'PENDING_TO_APPROVED' | 'PENDING_TO_REJECTED';

interface ExtensionProps {
  preClearanceId: number;
  preClearanceExtension: any;
  userType: string;
  canExtend: boolean;
  ExtensionwfActions: string[];
}

const Extension: React.FC<ExtensionProps> = ({
  preClearanceId,
  preClearanceExtension,
  canExtend,
  ExtensionwfActions,
}) => {
  const [isExtensionRequested, setIsExtensionRequested] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newExpirationDate, setNewExpirationDate] = useState<string>('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isExtensionCompleted, setIsExtensionCompleted] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isRejectConfirmModalVisible, setIsRejectConfirmModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const navigate = useNavigate();

  const approveAction = ExtensionwfActions?.find((action) => action === 'PENDING_TO_APPROVED');
  const rejectAction = ExtensionwfActions?.find((action) => action === 'PENDING_TO_REJECTED');

  const handleExtend = async () => {
    if (!newExpirationDate) {
      alert('Please select a valid expiration date.');
      return;
    }
    setIsConfirmModalVisible(true);
  };

  const handleConfirmExtension = async () => {
    try {
      const extensionData = { extension_exp: newExpirationDate, action: approveAction };
      await approveExtension(preClearanceId, extensionData);
      setIsExtensionRequested(true);
      setIsExtensionCompleted(true);
      setIsConfirmModalVisible(false);
      setIsModalVisible(false);
      navigate(`/pre-clearance`);
    } catch (error) {
      console.error('Error extending pre-clearance:', error);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please enter a reason for rejection.');
      return;
    }
    try {
      await pcExtensionWorkFlow(preClearanceId, rejectAction, rejectReason);
      setIsRejectConfirmModalVisible(false);
      setRejectReason(''); // reset after submission
      navigate(`/pre-clearance`);
    } catch (error) {
      console.error('Error rejecting pre-clearance:', error);
    }
  };

  const showRequestExtensionButton = preClearanceExtension === null && canExtend;

  // Each button is shown only if its specific action exists in ExtensionwfActions
  const showExtendButton = Boolean(approveAction);
  const showRejectButton = Boolean(rejectAction);

  const sharedButtonStyle = {
    backgroundColor: '#781B0D',
    color: 'white',
    borderRadius: '5px',
    height: '32px',
    fontSize: '14px',
    padding: '4px 12px',
    border: 'none',
    cursor: 'pointer',
  };

  return (
    <div>
      {/* Show "Request Extension" only when no extension exists and user is allowed */}
      {showRequestExtensionButton && !isExtensionRequested && !isExtensionCompleted && (
        <button
          onClick={() => setIsPopupOpen(true)}
          style={{
            ...sharedButtonStyle,
            marginBottom: '10px',
            fontWeight: 'bold',
          }}
        >
          Request Extension
        </button>
      )}

      {/* Render Extend and/or Reject based on their respective ExtensionwfActions */}
      {(showExtendButton || showRejectButton) && (
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Shown only if 'PENDING_TO_APPROVED' is in ExtensionwfActions */}
          {showExtendButton && (
            <BaseButton onClick={() => setIsModalVisible(true)} style={sharedButtonStyle}>
              Extend
            </BaseButton>
          )}

          {/* Shown only if 'PENDING_TO_REJECTED' is in ExtensionwfActions */}
          {showRejectButton && (
            <BaseButton onClick={() => setIsRejectConfirmModalVisible(true)} style={sharedButtonStyle}>
              Reject
            </BaseButton>
          )}
        </div>
      )}

      {/* Modal for selecting new expiration date */}
      <Modal
        title="Select New Expiration Date"
        open={isModalVisible}
        onOk={handleExtend}
        onCancel={() => {
          setIsModalVisible(false);
          setNewExpirationDate('');
        }}
        okText="Submit"
        cancelText="Cancel"
      >
        <DatePicker
          style={{ width: '100%' }}
          value={newExpirationDate ? moment(newExpirationDate) : null}
          onChange={(_, dateString) => setNewExpirationDate(dateString as string)}
          disabledDate={(current) => current && current < moment().endOf('day')}
        />
      </Modal>

      {/* Confirmation modal */}
      <Modal
        title="Confirm New Expiration Date"
        open={isConfirmModalVisible}
        onOk={handleConfirmExtension}
        onCancel={() => setIsConfirmModalVisible(false)}
        okText="Confirm"
        cancelText="Cancel"
      >
        <p>Are you sure you want to extend the expiration date to {newExpirationDate}?</p>
      </Modal>

      {/* Confirmation Modal for Rejection */}
      <Modal
        title="Confirm Rejection"
        open={isRejectConfirmModalVisible}
        onOk={handleConfirmReject}
        onCancel={() => {
          setIsRejectConfirmModalVisible(false);
          setRejectReason(''); // reset on cancel too
        }}
        okText="Confirm"
        cancelText="Cancel"
      >
        <p>Are you sure you want to reject this extension request?</p>
        <BaseInput.TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Enter reason for rejection..."
          rows={4}
          style={{
            width: '100%',
            marginTop: '12px',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
            resize: 'vertical',
            fontFamily: 'inherit',
            fontSize: '14px',
          }}
        />
      </Modal>
      {/* Extension request popup */}
      <ExtensionPopup
        visible={isPopupOpen}
        preClearanceId={preClearanceId}
        onClose={() => setIsPopupOpen(false)}
        onSubmit={() => {
          setIsPopupOpen(false);
          setIsExtensionRequested(true);
        }}
      />
    </div>
  );
};

export default Extension;
